// Miniatura cardului de proiect: volumul clădirii, izometric.
//
// Trei surse, în ordinea în care se știe ceva despre clădire:
//
//   1. SILUETA soluției de arhitectură — conturul real al fiecărui nivel, din
//      documentul canonic. Ăsta e desenul clădirii, nu o presupunere despre ea,
//      și e singurul lucru pe care îl au proiectele IMPORTATE (un DWG importat
//      n-are certificat de urbanism, deci n-are regim de înălțime).
//   2. REGIMUL din dosar — o cutie cu atâtea niveluri câte scrie în certificat.
//   3. Nimic — lotul punctat, adică exact ce se știe: că există un teren.
//
// La un proiect de STRUCTURĂ se desenează SCHELETUL, nu volumul alb: același
// contur, dar ca planșee pe fiecare nivel, cu stâlpii în colțuri, în culorile
// structurii din `paletaDesen.ts`. E ce primește proiectul, nu ce era deja
// desenat în planșe. Fără contur încă, scheletul e cel generic din
// `IlustratieProiect` — lotul n-ar spune nimic despre un proiect care nu
// pornește de la teren.
//
// Proiecția e cea din machetă (mock-ui): laturile la 30°, fața de est mai
// deschisă decât cea de sud, liniile de nivel trase pe fețele vizibile.
//
// Culorile: macheta e ALBĂ, cu fața din umbră gri deschis și conturul gri,
// pe un fundal gri spre teal și cu umbra ei purtată pe teren. Nuanța teal
// stă DOAR în miniatură: pe pagină ar face din listă un ecran monocrom.

import type { Silueta, TipProiect } from "../api";
import { PAL, alfa } from "../structure/paletaDesen";
import { desenTip } from "./IlustratieProiect";

const C = 0.8660254; // cos 30°
const S = 0.5; // sin 30°

/** FNV-1a: o amprentă stabilă pentru un id de proiect — aceeași clădire la
 *  fiecare încărcare a paginii, nu una nouă la fiecare randare. */
function amprenta(text: string): number {
  let h = 2166136261;
  for (let i = 0; i < text.length; i += 1) {
    h = Math.imul(h ^ text.charCodeAt(i), 16777619) >>> 0;
  }
  return h;
}

type Props = {
  cheie: string;
  niveluri: number | null;
  silueta?: Silueta | null;
  tip?: TipProiect;
  w?: number;
  h?: number;
};

export default function Volum({ cheie, niveluri, silueta, tip = "complet", w = 252, h = 120 }: Props) {
  const schelet = tip === "structura";
  if (silueta?.corpuri?.length) {
    return <Prisma cheie={cheie} silueta={silueta} schelet={schelet} w={w} h={h} />;
  }
  if (schelet) return <ScheletGeneric cheie={cheie} w={w} h={h} />;
  if (niveluri) return <Cutie cheie={cheie} niveluri={niveluri} w={w} h={h} />;
  return <Lot cheie={cheie} w={w} h={h} />;
}

// --- clădirea desenată din conturul ei real ---------------------------------

const UMBRA = "#dcdfe2"; // fața dinspre sud
const LUMINA = "#ffffff"; // fața dinspre est
const CAPAC = "#f6f7f8";
const CONTUR = "rgba(90, 96, 104, .42)";
const NIVEL = "rgba(90, 96, 104, .3)";
const UMBRA_PURTATA = "rgba(30, 60, 62, .16)";

/** Fundalul miniaturii: gri spre teal sus, mai deschis jos. `id` e al
 *  gradientului — unic pe card. */
function Fundal({ id, w, h }: { id: string; w: number; h: number }) {
  return (
    <>
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#dde6e6" />
          <stop offset="1" stopColor="#f2f5f5" />
        </linearGradient>
      </defs>
      <rect width={w} height={h} fill={`url(#${id})`} />
    </>
  );
}

/** Umbra purtată: poligoanele de la sol, estompate și împinse spre dreapta-jos. */
function Umbra({ id, dx, poligoane }: { id: string; dx: number; poligoane: string[] }) {
  return (
    <>
      <defs>
        <filter id={id} x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="5" />
        </filter>
      </defs>
      <g filter={`url(#${id})`} transform={`translate(${dx} 6)`}>
        {poligoane.map((pts, i) => <polygon key={i} fill={UMBRA_PURTATA} points={pts} />)}
      </g>
    </>
  );
}

function ton(nx: number, ny: number): string {
  const t = (nx - ny + 1) / 2;
  const mix = (a: number, b: number) => Math.round(a + (b - a) * t);
  const hex = (c: string) => [1, 3, 5].map((i) => parseInt(c.slice(i, i + 2), 16));
  const [r1, g1, b1] = hex(UMBRA);
  const [r2, g2, b2] = hex(LUMINA);
  return `rgb(${mix(r1, r2)},${mix(g1, g2)},${mix(b1, b2)})`;
}

function Prisma({ cheie, silueta, schelet, w, h }:
                { cheie: string; silueta: Silueta; schelet: boolean; w: number; h: number }) {
  const corpuri = silueta.corpuri.filter((c) => c.blocuri.length);

  // Încadrare în două treceri: proiectăm la scara 1, măsurăm cutia DESENULUI
  // (nu a modelului) și abia apoi scalăm. Fără asta, o clădire lungă și joasă
  // s-ar scala după înălțimea pe care n-o are.
  const util = { w: w - 60, h: h - 18 };
  const cutie = (cs: typeof corpuri) => {
    const pts: [number, number][] = [];
    for (const corp of cs) {
      for (const b of corp.blocuri) {
        for (const [x, y] of b.poly) {
          pts.push([(x - y) * C, (x + y) * S - b.z]);
          pts.push([(x - y) * C, (x + y) * S - (b.z + b.niveluri * b.h)]);
        }
      }
    }
    const xs = pts.map((p) => p[0]);
    const ys = pts.map((p) => p[1]);
    const x0 = Math.min(...xs);
    const x1 = Math.max(...xs);
    const y0 = Math.min(...ys);
    const y1 = Math.max(...ys);
    return { x0, x1, y0, y1,
             k: Math.min(util.w / Math.max(x1 - x0, 1e-6),
                         util.h / Math.max(y1 - y0, 1e-6)) };
  };

  // Un ansamblu întins pe 120 m încape în cadru doar făcut mic: douăsprezece
  // dungi de câțiva pixeli, în care nu se mai vede nicio clădire. Când
  // încadrarea pe tot costă atâta, se încadrează pe CORPUL CEL MAI ÎNALT —
  // ăla e clădirea despre care e proiectul — iar vecinii lui ies din cadru.
  const inaltimea = (corp: typeof corpuri[number]) =>
    Math.max(...corp.blocuri.map((b) => b.z + b.niveluri * b.h));
  const tot = cutie(corpuri);
  let vizor = tot;
  if (corpuri.length > 1) {
    const cel_mai_inalt = corpuri.reduce((a, b) => (inaltimea(b) > inaltimea(a) ? b : a));
    const singur = cutie([cel_mai_inalt]);
    if (singur.k > 1.7 * tot.k) vizor = singur;
  }
  const k = vizor.k;
  const ox = w / 2 - ((vizor.x0 + vizor.x1) / 2) * k;
  const oy = h / 2 - ((vizor.y0 + vizor.y1) / 2) * k;
  const P = (x: number, y: number, zz: number) =>
    `${(ox + (x - y) * C * k).toFixed(1)},${(oy + ((x + y) * S - zz) * k).toFixed(1)}`;

  // Corpurile se pun de la cel mai depărtat la cel mai apropiat, ca unul din
  // față să acopere ce e în spatele lui. În interiorul unui corp ordinea e de
  // jos în sus: consola etajului acoperă peretele parterului, nu invers.
  const ordonate = corpuri
    .map((corp) => ({
      corp,
      adanc: Math.max(...corp.blocuri.flatMap((b) => b.poly.map(([x, y]) => x + y))),
    }))
    .sort((a, b) => a.adanc - b.adanc);

  const desen: JSX.Element[] = schelet ? scheletul(ordonate.map((o) => o.corp), P) : [];
  if (!schelet) ordonate.forEach(({ corp }, ci) => {
    for (const [bi, b] of corp.blocuri.entries()) {
      const jos = b.z;
      const sus = b.z + b.niveluri * b.h;
      // Fețele care dau spatele privitorului nu se desenează; restul se pun de
      // la cea mai depărtată la cea mai apropiată, după vârful cel mai apropiat.
      const fete = [];
      for (let i = 0; i < b.poly.length; i += 1) {
        const a = b.poly[i];
        const c = b.poly[(i + 1) % b.poly.length];
        const lung = Math.hypot(c[1] - a[1], a[0] - c[0]) || 1;
        const nx = (c[1] - a[1]) / lung;
        const ny = (a[0] - c[0]) / lung;
        if (nx + ny <= 0.001) continue;
        fete.push({ a, c, nx, ny, adanc: Math.max(a[0] + a[1], c[0] + c[1]) });
      }
      fete.sort((p, q) => p.adanc - q.adanc);
      for (const [fi, f] of fete.entries()) {
        desen.push(
          <polygon key={`f${ci}-${bi}-${fi}`} fill={ton(f.nx, f.ny)}
                   stroke={CONTUR} strokeWidth={1} strokeLinejoin="round"
                   points={[P(f.a[0], f.a[1], jos), P(f.c[0], f.c[1], jos),
                            P(f.c[0], f.c[1], sus), P(f.a[0], f.a[1], sus)].join(" ")} />);
        for (let j = 1; j < b.niveluri; j += 1) {
          desen.push(
            <polyline key={`n${ci}-${bi}-${fi}-${j}`} fill="none"
                      stroke={NIVEL} strokeWidth={0.8}
                      points={[P(f.a[0], f.a[1], jos + j * b.h),
                               P(f.c[0], f.c[1], jos + j * b.h)].join(" ")} />);
        }
      }
      desen.push(
        <polygon key={`cap${ci}-${bi}`} fill={CAPAC} stroke={CONTUR} strokeWidth={1} strokeLinejoin="round"
                 points={b.poly.map(([x, y]) => P(x, y, sus)).join(" ")} />);
    }
  });

  const id = `sil${amprenta(cheie) % 99991}`;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: "block" }}
         aria-hidden="true">
      <Fundal id={`${id}c`} w={w} h={h} />
      {/* un schelet nu aruncă umbră plină: e aer între planșee */}
      {!schelet && (
        <Umbra id={id} dx={14}
               poligoane={ordonate.map(({ corp }) =>
                 corp.blocuri[0].poly.map(([x, y]) => P(x, y, 0)).join(" "))} />
      )}
      {desen}
    </svg>
  );
}

/** Scheletul corpurilor, de la cel mai depărtat la cel mai apropiat: pe fiecare
 *  bloc, de jos în sus, planșeul nivelului (cu grinzile pe contur) și stâlpii
 *  din colțuri până la următorul. Talpa e în culoarea fundației. */
function scheletul(corpuri: Silueta["corpuri"],
                   P: (x: number, y: number, z: number) => string): JSX.Element[] {
  const desen: JSX.Element[] = [];
  corpuri.forEach((corp, ci) => {
    for (const [bi, b] of corp.blocuri.entries()) {
      const stalpi = [...b.poly].sort((p, q) => p[0] + p[1] - (q[0] + q[1]));
      for (let j = 0; j <= b.niveluri; j += 1) {
        const z = b.z + j * b.h;
        const talpa = j === 0;
        desen.push(
          <polygon key={`p${ci}-${bi}-${j}`} strokeLinejoin="round"
                   fill={talpa ? FUNDATIE_UMBRA : PLANSEU}
                   stroke={talpa ? PAL.fundatie : PAL.grinda} strokeWidth={talpa ? 1 : 0.9}
                   points={b.poly.map(([x, y]) => P(x, y, z)).join(" ")} />);
        if (j === b.niveluri) continue;
        for (const [si, [x, y]] of stalpi.entries()) {
          desen.push(
            <polyline key={`s${ci}-${bi}-${j}-${si}`} fill="none" stroke={PAL.stalp}
                      strokeWidth={1.6} points={[P(x, y, z), P(x, y, z + b.h)].join(" ")} />);
        }
      }
    }
  });
  return desen;
}

// planșeul se vede prin el, ca în scena 3D; talpa, în tonul fundației
const PLANSEU = alfa(PAL.placa, 0.16);
const FUNDATIE_UMBRA = alfa(PAL.fundatie, 0.22);

/** Proiectul de structură fără nicio arhitectură încă: scheletul generic,
 *  același cu desenul de la alegerea tipului. */
function ScheletGeneric({ cheie, w, h }: { cheie: string; w: number; h: number }) {
  const id = `sch${amprenta(cheie) % 99991}`;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: "block" }}
         aria-hidden="true">
      <Fundal id={id} w={w} h={h} />
      {desenTip("structura", w, h, [18, 12])}
    </svg>
  );
}

// --- clădirea dedusă din regimul de înălțime --------------------------------

function Cutie({ cheie, niveluri, w, h }:
               { cheie: string; niveluri: number; w: number; h: number }) {
  const seed = amprenta(cheie);
  const n = Math.max(1, Math.min(niveluri, 30));
  let lat = 40 + (seed % 13); // latura lungă (spre stânga)
  let lung = 30 + ((seed >> 8) % 13); // latura scurtă (spre dreapta)
  let inalt = 9.5 * n;

  // Cutia se scalează ca să umple cadrul: altfel o casă P+1 e un punct în
  // mijlocul unei miniaturi goale, iar un bloc P+9 iese din ea.
  const k = Math.max(0.55, Math.min(2, Math.min((w - 96) / ((lat + lung) * C),
                                                (h - 18) / ((lat + lung) * S + inalt))));
  lat *= k;
  lung *= k;
  inalt *= k;
  const nivel = inalt / n;
  const y0 = h - 9;
  const cx = w / 2 - ((lung - lat) * C) / 2;
  const P = (a: number, b: number, sus = 0) =>
    `${(cx + lung * C * a - lat * C * b).toFixed(1)},${(y0 - lung * S * a - lat * S * b - sus).toFixed(1)}`;
  const fata = (pts: string[], fill: string) => (
    <polygon points={pts.join(" ")} fill={fill} stroke={CONTUR} strokeWidth={1}
             strokeLinejoin="round" />
  );

  const id = `vol${seed % 99991}`;

  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: "block" }}
         aria-hidden="true">
      <Fundal id={`${id}c`} w={w} h={h} />
      <Umbra id={id} dx={15} poligoane={[[P(0, 0), P(1, 0), P(1, 1), P(0, 1)].join(" ")]} />
      {fata([P(0, 0), P(1, 0), P(1, 0, inalt), P(0, 0, inalt)], LUMINA)}
      {fata([P(0, 0), P(0, 1), P(0, 1, inalt), P(0, 0, inalt)], UMBRA)}
      {fata([P(0, 0, inalt), P(1, 0, inalt), P(1, 1, inalt), P(0, 1, inalt)], CAPAC)}
      {Array.from({ length: n - 1 }, (_, i) => (
        <polyline key={i} fill="none" stroke={NIVEL} strokeWidth={0.8}
                  points={[P(1, 0, nivel * (i + 1)), P(0, 0, nivel * (i + 1)),
                           P(0, 1, nivel * (i + 1))].join(" ")} />
      ))}
    </svg>
  );
}

/** Lotul: se știe că există un teren, nu se știe încă ce se ridică pe el. */
function Lot({ cheie, w, h }: { cheie: string; w: number; h: number }) {
  const seed = amprenta(cheie);
  const lat = 72 + (seed % 16);
  const lung = 56 + ((seed >> 8) % 16);
  const y0 = h / 2 + ((lat + lung) * S) / 2;
  const cx = w / 2 - ((lung - lat) * C) / 2;
  const P = (a: number, b: number) =>
    `${(cx + lung * C * a - lat * C * b).toFixed(1)},${(y0 - lung * S * a - lat * S * b).toFixed(1)}`;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: "block" }}
         aria-hidden="true">
      <Fundal id={`lot${seed % 99991}`} w={w} h={h} />
      <polygon points={[P(0, 0), P(1, 0), P(1, 1), P(0, 1)].join(" ")}
               fill="none" stroke={CONTUR}
               strokeWidth={1.2} strokeDasharray="5 4" />
    </svg>
  );
}
